import { type BranchNode, type BranchNodeSection } from "@/lib/branchTypes"
import {
  DIAMOND_HALF, LINE_LENGTH, CURVE_HORIZONTAL_SPAN, MIN_SUBTREE_HEIGHT,
  MIN_LINE_LENGTH, PIXELS_PER_LINE,
} from "./timelineConstants"
export type MarkdownLineCounts = Record<string, number>

const CROSS_STREAM_VERTICAL_NUDGE_BY_NODE_ID: Record<string, number> = {}
const MIN_SUBTREE_HEIGHT_WITH_DOWNSTREAM = MIN_SUBTREE_HEIGHT + 4

export const markdownTimelineSegmentId = (nodeId: string) => `markdown:${nodeId}`
export const sectionTimelineSegmentId = (sectionId: string) => `section:${sectionId}`

const hasSegmentProgress = (
  segmentId: string,
  scrollProgressBySegmentId: Record<string, number>
) => {
  const segmentProgress = scrollProgressBySegmentId[segmentId]
  return segmentProgress !== undefined && segmentProgress > 0
}

export function getNodeTimelineSegmentIds(nodeId: string, node: BranchNode): string[] {
  const nodeTimelineSegmentIds: string[] = []
  if (node.markdownFile) nodeTimelineSegmentIds.push(markdownTimelineSegmentId(nodeId))
  const nodeSections = node.sections || []
  for (const nodeSection of nodeSections) {
    nodeTimelineSegmentIds.push(sectionTimelineSegmentId(nodeSection.id))
  }
  return nodeTimelineSegmentIds
}

export function hasNodeTimelineProgress(
  nodeId: string,
  node: BranchNode | undefined,
  scrollProgressBySegmentId: Record<string, number>
) {
  if (!node) return false
  const nodeTimelineSegmentIds = getNodeTimelineSegmentIds(nodeId, node)
  return nodeTimelineSegmentIds.some(nodeTimelineSegmentId => {
    return hasSegmentProgress(nodeTimelineSegmentId, scrollProgressBySegmentId)
  })
}

export function isSelectedBranchpointActive(
  nodeId: string,
  nodes: Record<string, BranchNode>,
  selectedPath: string[] | undefined,
  scrollProgressBySegmentId: Record<string, number> | undefined
) {
  if (!selectedPath || !scrollProgressBySegmentId) return false
  const node = nodes[nodeId]
  if (!node) return false
  const selectedNodeIndex = selectedPath.indexOf(nodeId)
  if (selectedNodeIndex === -1) return false
  const selectedChildId = selectedPath[selectedNodeIndex + 1]
  if (!selectedChildId || !getChildIds(node).includes(selectedChildId)) return false
  const downstreamSelectedNodeIds = selectedPath.slice(selectedNodeIndex + 1)
  return downstreamSelectedNodeIds.some(downstreamSelectedNodeId => {
    return hasNodeTimelineProgress(
      downstreamSelectedNodeId,
      nodes[downstreamSelectedNodeId],
      scrollProgressBySegmentId
    )
  })
}

export function getLineLength(
  markdownFile: string | undefined,
  lineCounts?: MarkdownLineCounts
): number {
  if (!markdownFile) return 0
  if (!lineCounts || !(markdownFile in lineCounts)) return LINE_LENGTH
  return Math.max(MIN_LINE_LENGTH, lineCounts[markdownFile] * PIXELS_PER_LINE)
}

export function getChildIds(node: BranchNode): string[] {
  return (node.branchPoints || [])
    .flatMap(bp => bp.options)
    .map(o => o.targetId)
    .filter((id): id is string => !!id)
}

export function getSubtreeHeight(
  nodeId: string,
  nodes: Record<string, BranchNode>,
  visited: Set<string> = new Set()
): number {
  if (visited.has(nodeId) || !nodes[nodeId]) return MIN_SUBTREE_HEIGHT
  const newVisited = new Set(visited)
  newVisited.add(nodeId)
  const childIds = getChildIds(nodes[nodeId])
  if (childIds.length === 0) return MIN_SUBTREE_HEIGHT
  return childIds.reduce(
    (sum, id) => sum + getSubtreeHeight(id, nodes, newVisited), 0
  )
}

export type SectionPosition = {
  section: BranchNodeSection
  diamondX: number
  lineStartX: number
  lineEndX: number
}

export function computeSectionPositions(
  sections: BranchNodeSection[], startX: number, lineCounts?: MarkdownLineCounts
): SectionPosition[] {
  let x = startX
  return sections.map(section => {
    const diamondX = x + DIAMOND_HALF
    const lineStartX = diamondX + DIAMOND_HALF
    const lineEndX = lineStartX + getLineLength(section.markdownFile, lineCounts)
    x = lineEndX
    return { section, diamondX, lineStartX, lineEndX }
  })
}

export type ChildPosition = { childId: string; targetX: number; targetY: number }

export function computeChildPositions(
  childIds: string[],
  nodes: Record<string, BranchNode>,
  visited: Set<string>,
  parentY: number,
  branchStartX: number,
  flipVertical?: boolean
): ChildPosition[] {
  if (flipVertical) childIds = [...childIds].reverse()
  const childHeights = childIds.map(id => getSubtreeHeight(id, nodes, new Set(visited)))
  const baseTargetYs: number[] = []
  const totalHeight = childHeights.reduce((a, b) => a + b, 0)
  let childY = parentY - totalHeight / 2
  for (const [i] of childIds.entries()) {
    const targetY = childY + childHeights[i] / 2
    baseTargetYs.push(targetY)
    childY += childHeights[i]
  }

  const leafChildIds = childIds.filter(id => {
    const node = nodes[id]
    if (!node) return false
    return getChildIds(node).length === 0
  })

  const leafTargetYById = new Map<string, number>()
  const leafTotalHeight = leafChildIds.length * MIN_SUBTREE_HEIGHT
  let leafY = parentY - leafTotalHeight / 2
  for (const leafChildId of leafChildIds) {
    leafTargetYById.set(leafChildId, leafY + MIN_SUBTREE_HEIGHT / 2)
    leafY += MIN_SUBTREE_HEIGHT
  }

  const targetYs = childIds.map((childId, i) => {
    const leafTargetY = leafTargetYById.get(childId)
    const baseY = leafTargetY ?? baseTargetYs[i]
    const nudge = CROSS_STREAM_VERTICAL_NUDGE_BY_NODE_ID[childId] ?? 0
    return baseY + nudge
  })
  const hasDownstreamByChildId = new Map<string, boolean>()
  for (const childId of childIds) {
    const childNode = nodes[childId]
    hasDownstreamByChildId.set(childId, !!childNode && getChildIds(childNode).length > 0)
  }

  for (const [i] of targetYs.entries()) {
    if (i === 0) continue
    const prevChildId = childIds[i - 1]
    const currentChildId = childIds[i]
    const prevHasDownstream = hasDownstreamByChildId.get(prevChildId) ?? false
    const currentHasDownstream = hasDownstreamByChildId.get(currentChildId) ?? false
    const minSpacing = prevHasDownstream || currentHasDownstream
      ? MIN_SUBTREE_HEIGHT_WITH_DOWNSTREAM
      : MIN_SUBTREE_HEIGHT
    const minY = targetYs[i - 1] + minSpacing
    if (targetYs[i] < minY) {
      targetYs[i] = minY
    }
  }

  const primaryChildIndex = childIds.findIndex(id => nodes[id]?.primary)
  if (primaryChildIndex !== -1) {
    const primaryAlignmentDelta = parentY - targetYs[primaryChildIndex]
    for (const [i] of targetYs.entries()) {
      targetYs[i] += primaryAlignmentDelta
    }
  }

  return childIds.map((childId, i) => {
    return { childId, targetX: branchStartX + CURVE_HORIZONTAL_SPAN, targetY: targetYs[i] }
  })
}

export function computePastCurrentBranchpoints(
  selectedPath: string[],
  nodes: Record<string, BranchNode>,
  scrollProgressBySegmentId: Record<string, number>
): Set<string> {
  const result = new Set<string>()
  let currentNodeIndex = -1
  const selectedPathNodeIds = [...selectedPath]
  for (let i = selectedPathNodeIds.length - 1; i >= 0; i--) {
    const selectedPathNodeId = selectedPathNodeIds[i]
    if (hasNodeTimelineProgress(
      selectedPathNodeId,
      nodes[selectedPathNodeId],
      scrollProgressBySegmentId
    )) {
      currentNodeIndex = i
      break
    }
  }
  if (currentNodeIndex < 0) currentNodeIndex = 0
  for (let i = currentNodeIndex; i < selectedPathNodeIds.length; i++) {
    const nodeId = selectedPathNodeIds[i]
    const node = nodes[nodeId]
    if (node && getChildIds(node).length > 1) {
      result.add(nodeId)
    }
  }
  return result
}

export function getSpineEndX(
  node: BranchNode, startX: number, lineCounts?: MarkdownLineCounts
): number {
  const sections = node.sections || []
  const childIds = getChildIds(node)
  let x = startX + DIAMOND_HALF
  if (sections.length === 0 && childIds.length === 0) return x
  x += getLineLength(node.markdownFile, lineCounts)
  for (const section of sections) {
    x += DIAMOND_HALF * 2 + getLineLength(section.markdownFile, lineCounts)
  }
  return x
}

export function getTreeWidth(
  nodeId: string,
  nodes: Record<string, BranchNode>,
  visited: Set<string> = new Set(),
  lineCounts?: MarkdownLineCounts
): number {
  if (visited.has(nodeId) || !nodes[nodeId]) return DIAMOND_HALF * 2
  const newVisited = new Set(visited)
  newVisited.add(nodeId)
  const node = nodes[nodeId]
  const sections = node.sections || []
  const childIds = getChildIds(node)
  let w = DIAMOND_HALF * 2
  if (sections.length > 0 || childIds.length > 0) {
    w += getLineLength(node.markdownFile, lineCounts)
    for (const section of sections) {
      w += DIAMOND_HALF * 2 + getLineLength(section.markdownFile, lineCounts)
    }
  }
  if (childIds.length > 0) {
    const childWidths = childIds.map(
      id => getTreeWidth(id, nodes, newVisited, lineCounts)
    )
    w += CURVE_HORIZONTAL_SPAN + Math.max(...childWidths)
  }
  return w
}
